/**
 * LoginModal — Phase 2.9 in-scene replacement for navigating to `/login`.
 *
 * Renders the shared `<LoginSteps>` flow inside a centred pixel-card on top of
 * a *translucent* `bg-pop-night/70` backdrop so the home scene stays visible
 * behind it. The avatar in the scene moves out of the centred card while this
 * is open (see `HomeScene.tsx` character-x table) so the user can watch the
 * `character-taking-picture` pose during the photo-login moment.
 *
 * Close affordances mirror `<UploadModal>`:
 *   - ESC key            → calls `onClose`
 *   - backdrop click     → calls `onClose`
 *   - corner X           → calls `onClose`
 *   - LoginSteps onCancel → calls `onClose`
 *
 * On a successful photo-login the embedded `LoginSteps` invokes `onSuccess`
 * with the new Session. The scene picks that up via the TanStack Query cache
 * invalidation in `usePhotoLogin` — once `useFamily()` re-fetches, the
 * `isLoggedIn` derived flag flips and the avatar starts waving on subsequent
 * clicks instead of re-opening this modal.
 *
 * `data-testid="login-modal"` is exposed for Playwright.
 */
import { useEffect } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { X } from 'lucide-react'

import LoginSteps from '@/components/LoginSteps'
import type { Session } from '@shared/schemas'

export type LoginModalProps = {
  open: boolean
  onClose: () => void
  onSuccess: (session: Session) => void
}

export function LoginModal({
  open,
  onClose,
  onSuccess,
}: LoginModalProps): JSX.Element {
  // ESC closes the modal. Effect runs once whenever `open` flips on so the
  // listener only exists while a modal is mounted.
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="login-modal"
          data-testid="login-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Přihlášení fotkou"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: 'linear' }}
          className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6"
        >
          {/* Backdrop — translucent so the scene shows through. Click closes. */}
          <button
            type="button"
            aria-label="Zavřít"
            data-testid="login-modal-backdrop"
            onClick={onClose}
            className="absolute inset-0 bg-pop-night/70 cursor-default"
          />

          {/* Card container — stops backdrop click propagation. */}
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.22, ease: [0, 0, 0, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-sm"
          >
            <button
              type="button"
              aria-label="Zavřít"
              data-testid="login-modal-close"
              onClick={onClose}
              className="absolute -top-3 -right-3 z-10 bg-pop-torch border-[3px] border-pop-silhouette p-1 cursor-pointer hover:bg-pop-cream"
            >
              <X className="h-4 w-4 text-pop-silhouette" strokeWidth={3} />
            </button>
            <LoginSteps onComplete={onSuccess} onCancel={onClose} />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default LoginModal
